/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps, Theme } from '@mui/material';
import React from 'react';
import { Menu } from '@opengeoweb/theme';
import { CustomToggleButton } from '../CustomToggleButton';
import { CustomTooltip } from '../CustomTooltip';

export interface LegendToggleButtonProps {
  isLegendVisible: boolean;
  onToggle: () => void;
  showTooltipText: string;
  hideTooltipText: string;
  legendLabel: string;
  /** Container-query responsive styles — pass the container-specific breakpoint rules here */
  sx?: SxProps<Theme>;
}

export const LegendToggleButton = ({
  isLegendVisible,
  onToggle,
  showTooltipText,
  hideTooltipText,
  legendLabel,
  sx,
}: LegendToggleButtonProps): React.ReactElement => (
  <CustomTooltip title={isLegendVisible ? hideTooltipText : showTooltipText}>
    <CustomToggleButton
      aria-label={isLegendVisible ? hideTooltipText : showTooltipText}
      onClick={onToggle}
      selected={isLegendVisible}
      size="small"
      sx={[
        {
          height: '10px !important',
          '& span': {
            fontSize: '9pt',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      variant="tool"
    >
      <Menu />
      <span className="longText">{legendLabel}</span>
    </CustomToggleButton>
  </CustomTooltip>
);
